import Vue from 'vue'
import router from '@/router'
import to from 'await-to-js'
import client from '@/api/client'
import ProductDataService from '@/api/services/_shared/product-data-service.js'
import productGroupReviewsQuery from '@/api/gql/product/query/product-group-reviews.gql'
import {
  redirectToFilterPage,
  findVariantIdByRouteColors,
  orderVariantsByNewColors,
} from '@/stores/product-store-helpers.js'
import { loadForAtLeast } from '@/helpers/request'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import routeNames from '@/router/route-names'
import { GENDERS } from '@/stores/product.js'

export default {
  replaceRoute({ rootState, state, getters }, isVariantClick = true) {
    router.replace({
      name: routeNames.product,
      params: {
        ...rootState.route.params,
        gender: state.selectedGender,
        colors: getters.selectedColors,
        isVariantClick,
      },
    })
  },

  validateRoute({ rootState, getters }) {
    const routeColors = rootState.route.params.colors?.split('-') || []
    const isValidColors = !!findVariantIdByRouteColors(getters.product.variants, routeColors)
    const isValidGender = Object.values(GENDERS).includes(rootState.route.params.gender)

    if (!isValidColors || !isValidGender) {
      // Fallback to main variant which is the first male variant
      router.replace({
        name: routeNames.product,
        query: rootState.route.query,
        params: { isVariantClick: true },
      })
    }
  },

  handleGenderSwitch({ state, getters, commit, dispatch }, gender) {
    if (gender === state.selectedGender) return
    commit('setGender', gender)
    const currentVariantId =
      gender === GENDERS.mens ? getters.firstMaleVariantId : getters.firstFemaleVariantId
    commit('setCurrentVariantId', currentVariantId)
    commit('resetSlideIndex')
    if (!state.isRouteUpdatingBlocked) dispatch('replaceRoute')
    notifyExternalServices({
      action: ACTIONS.productInteraction,
      details: { action: 'gender-select', label: gender },
    })
  },

  async fetchData({ rootState, commit, dispatch, getters, state }, payload = {}) {
    const [error, result] = await to(ProductDataService.fetch({ slug: payload.slug }))
    if (error || !result || !result.data?.productGroup?.productData?.variants.length)
      return redirectToFilterPage(rootState.page.hostname, rootState.page.locale)

    const { productGroup, sizeInfo: { isSizeInfoVisible = false } = {} } = result.data
    commit('setReviewRatingAverage', productGroup.reviewRatingAverage)
    commit('setReviewTotal', productGroup.reviewTotal)
    delete productGroup.reviewRatingAverage
    delete productGroup.reviewTotal
    commit('setProductGroup', orderVariantsByNewColors(productGroup))
    if (getters.isRaffle) dispatch('page/startUpdatingCurrentTime', null, { root: true })
    commit('setSizeInfo', isSizeInfoVisible)
    notifyExternalServices({
      action: ACTIONS.sizeInfo,
      details: { isSizeInfoVisible },
    })
    if (payload.isRouteUpdatingBlocked) commit('blockRouteUpdating')
    if (!state.isRouteUpdatingBlocked) dispatch('validateRoute')
  },

  async fetchReviews({ commit, getters }) {
    const {
      data: { productGroup },
    } = await client.query({
      query: productGroupReviewsQuery,
      variables: {
        slug: getters.productGroupSlug,
      },
    })
    commit('setHasFetchedFirstReviews', true)

    if (productGroup) {
      Vue.nextTick(() => commit('setReviews', productGroup.reviews))
    }
  },

  async loadMoreReviews({ state, commit, getters }) {
    commit('setIsLoadingMoreReviews', true)
    const {
      data: { productGroup },
    } = await loadForAtLeast(
      client.query({
        query: productGroupReviewsQuery,
        variables: {
          slug: getters.productGroupSlug,
          reviewPage: state.reviewPageLoaded + 1,
        },
      })
    )
    commit('setIsLoadingMoreReviews', false)
    commit('addReviews', productGroup.reviews)
  },
}
